import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GQLQueryOrderInput, GQLQueryOrderValue } from "@rx-cs-system/schema";
import type { ReactElement, ReactNode } from "react";
import { useCallback } from "react";
import styled from "styled-components";

import { hexToRGB } from "../../utils/style";
import { TypographyProps, TypographyStyle } from "../atoms/Typography";
import { defaultTheme } from "../themes";

/**
 * [共通パーツ]テーブルリスト
 */

type DataTableProps = {
  children: ReactNode;
};

const StyledTable = styled.table`
  border-radius: 6px;
  overflow: hidden;
  background-color: ${(props) => props.theme.colors.primaryBackground};
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  tr {
    display: flex;
    padding-left: 10px;
    padding-right: 10px;
  }
  th,
  td {
    display: flex;
    vertical-align: middle;
    text-align: left;
    white-space: nowrap;
    padding-left: 10px;
    padding-right: 10px;
  }
`;

export function DataTable(props: DataTableProps) {
  return <StyledTable>{props.children}</StyledTable>;
}

// flex-end = right
type Align = "center" | "left" | "flex-end";

/**
 * Header
 */
type DataListHeaderProps = {
  row: {
    props: {
      children: ReactElement<DataListHeaderItemProps>[];
    };
  };
  flexRatio: number[];
};

const StyledHeader = styled.thead`
  background-color: ${(props) =>
    hexToRGB(props.theme.colors.secondaryBackground, 0.08)};
  tr {
    height: 45px;
  }
`;

const StyledHeaderItem = styled.th<{
  $flex: number;
  $alignItems?: Align;
}>`
  flex-grow: ${(props) => props.$flex};
  width: 0;
  ${({ $alignItems }) => $alignItems && `justify-content: ${$alignItems};`}
`;

export function DataListHeader(props: DataListHeaderProps) {
  const { row, flexRatio } = props;
  return (
    <StyledHeader>
      <tr>
        {row.props.children.map(
          (item: ReactElement<DataListHeaderItemProps>, index: number) => (
            <StyledHeaderItem
              $flex={flexRatio[index]}
              key={index}
              $alignItems={item.props.align}
            >
              {item}
            </StyledHeaderItem>
          ),
        )}
      </tr>
    </StyledHeader>
  );
}

/**
 * DataList Simple Header
 */

type DataListSimpleHeaderProps = {
  titles: string[];
  flexRatio: number[];
};

const StyledSimpleTableRow = styled.tr`
  padding: 12px 10px;
  border-bottom: 2px solid ${(props) => props.theme.colors.primaryBorder};
`;

const StyledSimpleTableHeader = styled.th<
  TypographyProps & {
    $bold: boolean;
    $flex: number;
  }
>`
  flex-grow: ${(props) => props.$flex};
  width: 0;
  ${TypographyStyle}
`;

export function DataListSimpleHeader(props: DataListSimpleHeaderProps) {
  const { titles, flexRatio } = props;

  return (
    <thead>
      <StyledSimpleTableRow>
        {titles.map((title: string, index: number) => (
          <StyledSimpleTableHeader
            $flex={flexRatio[index]}
            key={index}
            $bold
            size={2}
          >
            {title}
          </StyledSimpleTableHeader>
        ))}
      </StyledSimpleTableRow>
    </thead>
  );
}

/**
 * HeaderItem
 */
type DataListHeaderItemProps = {
  children: ReactElement<DataListHeaderItemProps>;
  onSort?: () => void;
  order?: GQLQueryOrderValue;
  align?: Align;
};

const IconWrapper = styled.div<{ opacity: number }>`
  color: ${(props: { opacity: number }) =>
    hexToRGB(defaultTheme.colors.secondaryBackground, props.opacity)};
`;

const HeaderItemWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover ${IconWrapper} {
    color: ${hexToRGB(defaultTheme.colors.secondaryBackground, 0.54)};
  }
`;

const TitleInner = styled.div`
  opacity: 90%;
  margin-right: 15px;
`;

export const SwitchHeaderOrder = (
  field: string,
  order?: GQLQueryOrderInput,
) => ({
  field,
  value:
    order?.field !== field
      ? GQLQueryOrderValue.Desc
      : order.value === GQLQueryOrderValue.Asc
        ? GQLQueryOrderValue.Desc
        : GQLQueryOrderValue.Asc,
});

export function DataListHeaderItem(props: DataListHeaderItemProps) {
  const { onSort, order } = props;

  return (
    <HeaderItemWrapper onClick={onSort}>
      <TitleInner>{props.children}</TitleInner>
      <IconWrapper opacity={order === undefined ? 0.1 : 0.54}>
        {onSort && (
          <FontAwesomeIcon
            icon={"caret-up"}
            rotation={order === GQLQueryOrderValue.Asc ? undefined : 180}
          />
        )}
      </IconWrapper>
    </HeaderItemWrapper>
  );
}

/**
 * Body
 */
type DataListBodyProps = {
  rows: ReactElement<DataListRowProps>[];
};

const StyledBody = styled.tbody`
  overflow-y: auto;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  tr {
    border-bottom: 1px solid ${(props) => props.theme.colors.primaryBorder};
    cursor: pointer;
    &:hover {
      background-color: ${(props) => props.theme.colors.hoverBackground};
    }
    z-index: 1;
  }
`;

export function DataListBody({ rows }: DataListBodyProps) {
  const rowClick = useCallback((row: ReactElement<DataListRowProps>) => {
    row.props.onRowClick && row.props.onRowClick();
  }, []);

  const mouseOver = useCallback((row: ReactElement<DataListRowProps>) => {
    row.props.onMouseOver && row.props.onMouseOver();
  }, []);

  const mouseLeave = useCallback((row: ReactElement<DataListRowProps>) => {
    row.props.onMouseLeave && row.props.onMouseLeave();
  }, []);

  return (
    <StyledBody>
      {rows.map((row: ReactElement<DataListRowProps>, index: number) => (
        <tr
          key={index}
          onClick={() => rowClick(row)}
          onMouseOver={() => mouseOver(row)}
          onMouseLeave={() => mouseLeave(row)}
        >
          {row}
        </tr>
      ))}
    </StyledBody>
  );
}

/**
 * Row
 */
type DataListRowProps = {
  children: ReactElement[];
  flexRatio: number[];
  onRowClick?: () => void;
  onMouseOver?: () => void;
  onMouseLeave?: () => void;
};

const BodyItemWrapper = styled.td<{ $flex: number }>`
  min-height: 58px;
  align-items: center;
  flex-grow: ${(props) => props.$flex};
  width: 0;
`;

export function DataListRow(props: DataListRowProps) {
  const { children, flexRatio } = props;
  return (
    <>
      {children.map((item: ReactElement, index: number) => (
        <BodyItemWrapper key={index} $flex={flexRatio[index]}>
          {item}
        </BodyItemWrapper>
      ))}
    </>
  );
}

/**
 * BodyItem
 */
type DataListBodyItemProps = {
  children: ReactNode;
  textAlign?: TextAlign;
};

type TextAlign = "left" | "center" | "right";

const StyledBodyItem = styled.div<{
  textAlign?: TextAlign;
}>`
  align-items: center;
  text-align: ${({ textAlign }) => textAlign || "left"};
  flex-grow: 2;
  width: 0;
`;

export function DataListBodyItem(props: DataListBodyItemProps) {
  return (
    <StyledBodyItem textAlign={props.textAlign}>
      {props.children}
    </StyledBodyItem>
  );
}
